// Each slot carries the date it sits on now; the body's `serviceDate` is the destination.
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { StepResult } from '../types'
import type { DatedRouteSlot } from 'types/api/route/routeSlotRequests'
import type { RouteId, ServiceDate } from 'types/entities/shared/ids'
import type { Deps, PipelineStep } from 'types/modules/routing'

export interface ChangeDateRouteSlotStepArgs {
  readonly routeId: RouteId
  readonly toServiceDate: ServiceDate
  readonly slots: ReadonlyArray<DatedRouteSlot>
}

export type ChangeDateRouteSlotStepValue = { readonly stopCount: number }

export type ChangeDateRouteSlotStepResult = StepResult<'CHANGE_DATE_DAILY', ChangeDateRouteSlotStepValue>

export const makeChangeDateRouteSlotStep = (
  args: ChangeDateRouteSlotStepArgs,
  deps: Deps
): PipelineStep<'CHANGE_DATE_DAILY', ChangeDateRouteSlotStepResult> => {
  const stepId = `change-date-route-slot:${args.routeId}:${args.toServiceDate}`
  return {
    id: stepId,
    phase: 'CHANGE_DATE_DAILY',
    label: 'Failed to move stops to another date',
    applyOrder: 100,
    run: async () => {
      if (!deps.changeDateRouteSlot) {
        return err({ kind: 'PRECONDITION_FAILED', reason: 'changeDateRouteSlot dep not provided — the v3-slot strategy requires it' })
      }
      if (args.slots.length === 0) {
        return ok({ phase: 'CHANGE_DATE_DAILY', stepId, value: { stopCount: 0 } })
      }

      try {
        const response = await deps
          .changeDateRouteSlot({
            routeId: args.routeId,
            data: { stops: [...args.slots], serviceDate: args.toServiceDate },
            skipInvalidate: true,
          })
          .unwrap()
        return ok({ phase: 'CHANGE_DATE_DAILY', stepId, value: { stopCount: response?.stopCount ?? 0 } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({ kind: 'BE_REJECTED', phase: 'CHANGE_DATE_DAILY', status, message })
      }
    },
  }
}
